import { chordSheetTitle, compareChordSheets } from "@bandstand/core/ChordSheet";
import type { ChordSheet } from "@bandstand/core/ChordSheet";
import type { Library } from "@bandstand/core/Library";
import type { Setlist } from "@bandstand/core/Setlist";
import type { Song } from "@bandstand/core/Song";
import { action, observable } from "mobx";

/** What's in the library, for the screens to show. Changes go through `change()`, which loads everything again after. */
export class LibraryStore {
    @observable accessor songs: Song[] = [];

    @observable accessor chordSheets: ChordSheet[] = [];

    @observable accessor setlists: Setlist[] = [];

    @observable accessor loaded = false;

    constructor(private readonly library: Library) {}

    async load(): Promise<void> {
        const [songs, chordSheets, setlists] = await Promise.all([
            this.library.songs(),
            this.library.allChordSheets(),
            this.library.setlists(),
        ]);

        this.update(songs, chordSheets, setlists);
    }

    /** Changes the library, then loads it again so every screen shows the change. */
    async change<T>(work: (library: Library) => Promise<T>): Promise<T> {
        const result = await work(this.library);

        await this.load();

        return result;
    }

    /** The ChordPro text of a chord sheet. */
    async text(chordSheetId: string): Promise<string> {
        return this.library.text(chordSheetId);
    }

    song(id: string | undefined): Song | undefined {
        return this.songs.find((song) => song.id === id);
    }

    /** The songs whose title or artist contains what's searched for, ignoring capitals. */
    findSongs(query: string): Song[] {
        const search = query.trim().toLowerCase();

        return this.songs.filter((song) => `${song.title} ${song.artist ?? ""}`.toLowerCase().includes(search));
    }

    chordSheet(id: string | undefined): ChordSheet | undefined {
        return this.chordSheets.find((sheet) => sheet.id === id);
    }

    setlist(id: string | undefined): Setlist | undefined {
        return this.setlists.find((setlist) => setlist.id === id);
    }

    /** A song's chord sheets, the first one first. Setlist-only ones are left out unless asked for. */
    versionsOf(songId: string, withSetlistOnly = false): ChordSheet[] {
        return this.chordSheets
            .filter((sheet) => sheet.songId === songId && (withSetlistOnly || !sheet.setlistId))
            .sort(compareChordSheets);
    }

    /** The name people see for a chord sheet, like "Amazing Grace, acoustic". */
    title(chordSheetId: string): string {
        const sheet = this.chordSheet(chordSheetId);
        const song = this.song(sheet?.songId);

        return sheet && song ? chordSheetTitle(sheet, song) : "";
    }

    /** The setlists that play a chord sheet. */
    setlistsPlaying(chordSheetId: string): Setlist[] {
        return this.setlists.filter((setlist) => setlist.items.some((item) => item.chordSheetId === chordSheetId));
    }

    @action
    private update(songs: Song[], chordSheets: ChordSheet[], setlists: Setlist[]): void {
        this.songs = songs;
        this.chordSheets = chordSheets;
        this.setlists = setlists;
        this.loaded = true;
    }
}
